import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterBar from "components/common/FilterBar";
import { useTranslation } from "react-i18next";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "assets/icons";
import { usePermission } from "hooks/usePermission";
import TableShimmer from "components/common/TableShimmer";
import NoDataFound from "components/common/NoDataFound";
import { PLAN_OPTIONS, WASH_HISTORY_STATUS } from "utils/constants";
import Paginations from "components/common/Paginations";
import { WashHistoryStatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import {
  getWashHistory,
  resetFilters,
  setFilter,
} from "store/slices/washHistorySlice";
import { getPlansList } from "store/slices/membershipSlice";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getBranchesList } from "store/slices/branchesSlice";
import PageHeader from "components/common/PageHeader";
import { usePageParam } from "hooks/usePageParam";

const WashHistory = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { view, noWashHistory } = Icons || {};
  const dispatch = useDispatch();
  const [pageParam, setPageParam] = usePageParam();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView } = canAction("wash_history");

  const {
    items,
    loading,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, userId, branchId, planId },
  } = useSelector((state) => state.WashHistory);
  const { plansList } = useSelector((state) => state.Membership);
  const { itemsList: branchesList } = useSelector((state) => state.Branches);

  useEffect(() => {
    if (canView) {
      getAllData(pageParam);
      dispatch(getPlansList());
      dispatch(getBranchesList());
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    setPageParam(page || 1);
    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset || status == -1 ? "" : status,
      keyword: isResetKeyword ? "" : keyword || "",
      userId: isReset ? "" : userId,
      branchId: isReset ? "" : branchId,
      planId: isReset ? "" : planId,
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getWashHistory(payload));
  };

  const handleReset = useCallback(() => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  }, []);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: WASH_HISTORY_STATUS,
      },
      {
        key: "planId",
        placeholder: t("Select Plan"),
        type: "singleSelect",
        options: formatOptionsForSelect(plansList, language, "title"),
      },
      {
        key: "branchId",
        placeholder: t("Select Branch"),
        type: "singleSelect",
        options: formatOptionsForSelect(branchesList, language, "name"),
      },
    ],
    [t, plansList, branchesList],
  );

  const columns = useMemo(
    () => [
      {
        header: t("Customer Name"),
        key: "userId",
        render: (value) => value?.name || "-",
      },
      {
        header: t("Customer Phone Number"),
        key: "userId",
        render: (value) => value?.phone,
      },
      // {
      //   header: t("Vehicle Name"),
      //   key: "userVehicleId",
      //   render: (value) => value?.vehicleName,
      // },
      {
        header: t("Number Plate"),
        key: "licensePlateNumber",
      },
      {
        header: t("Branch Name"),
        key: "branchId",
        render: (value) =>
          language === "ar" ? value?.name?.ar : value?.name?.en,
      },
      {
        header: t("Wash Type"),
        key: "userMembershipId",
        render: (value) => {
          const name = PLAN_OPTIONS?.find(
            (option) => option?.value === value?.planType,
          )?.name;
          return name ? t(name) : "-";
        },
      },
      {
        header: t("Plan Name"),
        key: "planId",
        render: (value) =>
          language === "ar" ? value?.title?.ar : value?.title?.en,
      },
      {
        header: t("Wash Started Date"),
        key: "startedAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Wash Completed Date"),
        key: "completedAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <WashHistoryStatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        <Link to={`/wash-history/wash-history-details?id=${item._id}`}>
          <img src={view} width={20} height={20} alt="view" />
        </Link>
      </>
    ),
    [],
  );

  return (
    <div className="page-content wash_history_page">
      <PageHeader title={t("Wash History")} />

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name, Phone Number, Number Plate")}
        searchMaxWidth={370}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, planId, branchId }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
      />
      {loading && <TableShimmer rows={12} columns={8} />}
      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No wash history found")} icon={noWashHistory} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default WashHistory;
